import { caracteres } from './normalizacao';

/**
 * Encontros vocálicos: ditongo e hiato (§22.7).
 *
 * **Isto é uma aproximação, e é de propósito.** Separar ditongo de hiato com
 * rigor exige silabação, que em português depende de tonicidade, de origem da
 * palavra e de casos que os próprios gramáticos disputam. O jogo não precisa
 * disso: precisa de um critério estável, explicável numa frase e que acerte a
 * esmagadora maioria — se `CAOS` conta como uma coisa ou outra, o que importa
 * é que conte sempre igual.
 *
 * O critério, sobre cada par de vogais vizinhas:
 *
 *  1. nasal na frente (`ÃO`, `ÃE`, `ÕE`) é **ditongo** — `PÃO`, `MÃE`, `PÕE`;
 *  2. `Í` ou `Ú` acentuados no par é **hiato**, e é justamente para isso que o
 *     acento está ali — `SAÍDA`, `BAÚ`, `PAÍS`;
 *  3. duas fortes (`A`, `E`, `O` e suas acentuadas) é **hiato** — `POETA`,
 *     `VOO`, `COELHO`;
 *  4. o resto, que é sempre uma forte com um `I`/`U` átono, é **ditongo** —
 *     `PAI`, `MEU`, `VIU`, `ÁGUA`.
 *
 * A exceção é o `U` mudo de `QUE`, `QUI`, `GUE`, `GUI`: ele não se pronuncia e
 * não forma encontro nenhum. `QUEIJO` continua tendo ditongo, mas por causa do
 * `EI`, não do `UE`.
 */

const VOGAIS = 'AEIOUÁÀÂÃÉÊÍÓÔÕÚÜ';
/** Fortes: as que não podem ser semivogais. */
const FORTES = 'AEOÁÀÂÉÊÓÔ';
const NASAIS = 'ÃÕ';
/** Fracas acentuadas — o acento aqui existe para desfazer o ditongo. */
const FRACAS_ACENTUADAS = 'ÍÚ';

function ehVogal(caractere: string): boolean {
  return VOGAIS.includes(caractere);
}

interface Par {
  readonly primeira: string;
  readonly segunda: string;
  /** Caractere imediatamente anterior ao par, para o `QU`/`GU` mudo. */
  readonly anterior: string | undefined;
}

function paresVocalicos(texto: string): readonly Par[] {
  const letras = caracteres(texto.toUpperCase());
  const pares: Par[] = [];

  for (let i = 0; i + 1 < letras.length; i += 1) {
    const primeira = letras[i];
    const segunda = letras[i + 1];
    if (primeira === undefined || segunda === undefined) continue;
    if (!ehVogal(primeira) || !ehVogal(segunda)) continue;
    pares.push({ primeira, segunda, anterior: letras[i - 1] });
  }
  return pares;
}

/** O `U` de `QUE`, `QUI`, `GUE`, `GUI`: escrito, mudo, e sem encontro nenhum. */
function uMudo(par: Par): boolean {
  return (
    (par.anterior === 'Q' || par.anterior === 'G') &&
    par.primeira === 'U' &&
    'EI'.includes(par.segunda)
  );
}

function ehDitongo(par: Par): boolean {
  if (uMudo(par)) return false;
  if (NASAIS.includes(par.primeira)) return true;
  if (FRACAS_ACENTUADAS.includes(par.primeira) || FRACAS_ACENTUADAS.includes(par.segunda)) {
    return false;
  }
  return !(FORTES.includes(par.primeira) && FORTES.includes(par.segunda));
}

function ehHiato(par: Par): boolean {
  if (uMudo(par)) return false;
  if (NASAIS.includes(par.primeira)) return false;
  if (FRACAS_ACENTUADAS.includes(par.primeira) || FRACAS_ACENTUADAS.includes(par.segunda)) {
    return true;
  }
  return FORTES.includes(par.primeira) && FORTES.includes(par.segunda);
}

export function temDitongo(texto: string): boolean {
  return paresVocalicos(texto).some(ehDitongo);
}

export function temHiato(texto: string): boolean {
  return paresVocalicos(texto).some(ehHiato);
}
